/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion -- DIRECT-239681 */

import type { AnyApi } from '../buildCreateApi';
import type { EndpointCreateDataProvider } from '../endpointDataProvider';
import type {
    ApiEndpointMutation,
    ApiEndpointQuery,
    BaseQueryFn,
    EndpointDefinitions,
    InternalSerializeQueryArgs,
    Module,
    MutationHooks,
    QueryHooks,
} from '../rtk-query-types';
import type { GetStoreFn } from '../types';
import { validateKeepUnusedDataFor } from '../utils';

import { buildEndpointQuery, EndpointQuery } from './buildEndpointQuery';
import { buildEndpointQueryFlow, EndpointQueryFlow } from './buildEndpointQueryFlow';
import { buildEndpointMutate, EndpointMutate } from './buildEndpointMutate';
import { buildEndpointMutationState, EndpointMutationState } from './buildEndpointMutationState';
import { buildEndpointPrefetch, buildPrefetchKeyBuilder, EndpointPrefetch } from './buildEndpointPrefetch';
import { buildEndpointSubscribe, EndpointSubscribe } from './buildEndpointSubscribe';
import { buildSSRCompatibleQueryHook, isQueryDefinition } from './buildSSRCompatibleQueryHook';
import { buildEndpointUtils, EndpointUtils } from './buildEndpointUtils';
import { buildMiddlewareForServer } from './serverMiddleware';
import type {
    AnyMutationDefinition,
    AnyQueryDefinition,
    EndpointLazyQueryHook,
    EndpointQueryHook,
    EndpointQueryStateHook,
    EndpointsRootQueryHooks,
    EndpointsRootLazyQueryHooks,
    EndpointsRootQueryMethods,
    EndpointsRootMutationHooks,
    EndpointsRootMutationMethods,
} from './types';
import {
    buildEndpointMutationGetCachedValue,
    buildEndpointMutationUseCachedValues,
    buildEndpointQueryGetCachedValue,
    buildEndpointQueryUseCachedValues,
    type MutationEndpointCachedValues,
    type QueryEndpointCachedValues,
} from './buildEndpointCachedValues';

/** Тип всех методов, доступных в корне API (с корректными названиями) */
export type EndpointsRoot<Definitions extends EndpointDefinitions> = EndpointsRootQueryHooks<Definitions> &
    EndpointsRootLazyQueryHooks<Definitions> &
    EndpointsRootQueryMethods<Definitions> &
    EndpointsRootMutationMethods<Definitions> &
    EndpointsRootMutationHooks<Definitions>;

/** Тип всех методов и хуков доступных во всех эндпоинтах */
export type EndpointsMethodsAndHooks<Definitions extends EndpointDefinitions> = {
    [K in keyof Definitions]: Definitions[K] extends AnyQueryDefinition
        ? QueryEndpointMethodsAndHooks<Definitions[K]>
        : Definitions[K] extends AnyMutationDefinition
          ? MutationEndpointMethodsAndHooks<Definitions[K]>
          : never;
};

/** Тип всех методов и хуков в query-эндпоинте  */
export type QueryEndpointMethodsAndHooks<Definition extends AnyQueryDefinition> = EndpointQuery<Definition> &
    EndpointQueryFlow<Definition> &
    EndpointPrefetch<Definition> &
    EndpointSubscribe<Definition> &
    EndpointQueryHook<Definition> &
    EndpointLazyQueryHook<Definition> &
    EndpointQueryStateHook<Definition> &
    QueryEndpointCachedValues<Definition> &
    EndpointCreateDataProvider<Definition> &
    EndpointUtils<Definition>;

/** Тип всех методов и хуков в mutation-эндпоинте  */
export type MutationEndpointMethodsAndHooks<Definition extends AnyMutationDefinition> = EndpointMutate<Definition> &
    EndpointMutationState &
    MutationEndpointCachedValues<Definition>;

const apiSSRModuleName = Symbol();
type ApiSSRModule = typeof apiSSRModuleName;

/**
 * Каждый RTK Query модуль декларирует тип, которым расширяет полученное RTK Query API.
 * Этот модуль лишь оборачивает существующие хуки, поэтому расширение пустое.
 *
 * @see https://github.com/reduxjs/redux-toolkit/blob/master/packages/toolkit/src/query/react/module.ts
 */
declare module '@reduxjs/toolkit/query' {
    interface ApiModules<
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- DIRECT-239681
        BaseQuery extends BaseQueryFn,
        Definitions extends EndpointDefinitions,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- DIRECT-239681
        ReducerPath extends string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- DIRECT-239681
        TagTypes extends string,
    > {
        [apiSSRModuleName]: EndpointsRoot<Definitions> & {
            endpoints: EndpointsMethodsAndHooks<Definitions>;
        };
    }
}

/**
 * @deprecated Закапываем в пользу query() методов в корне эндпоинта
 * @param name Название эндпоинта
 * @returns Название хука
 */
function buildHookName(name: string): `use${string}Query` {
    const capitalizedName = name.replace(name[0]!, name[0]!.toUpperCase());

    return `use${capitalizedName}Query`;
}

/**
 * @param name Название эндпоинта
 * @returns Название query-метода
 */
function buildQueryMethodName(name: string): `${string}Query` {
    const normalizedName = name.replace(name[0]!, name[0]!.toLowerCase());

    return `${normalizedName}Query`;
}

/**
 * @param name Название эндпоинта
 * @returns Название mutation-метода
 */
function buildMutationMethodName(name: string): `${string}Mutation` {
    const normalizedName = name.replace(name[0]!, name[0]!.toLowerCase());

    return `${normalizedName}Mutation`;
}

// Параметры для `apiSSRModule()`
type ApiSSRModuleOptions = {
    getStore: GetStoreFn;
};

/**
 * RTK Query модуль добавляющий поддержку SSR и Suspense в `useQuery` хук.
 *
 * @internal
 */
export function apiSSRModule({ getStore }: ApiSSRModuleOptions): Module<ApiSSRModule> {
    return {
        name: apiSSRModuleName,
        init(api, { serializeQueryArgs }, context) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            const anyApi = api as unknown as AnyApi & { [key: string]: unknown };
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            const internalSerializeQueryArgs = serializeQueryArgs as InternalSerializeQueryArgs;

            if (process.env.BUILD_TARGET !== 'client') {
                // На сервере нет смысла использовать оригинальный middleware, поскольку в нем
                // реализована логика по очистке данных по таймауту и поллинг, которые на сервере
                // вызывают утечки памяти, но практической пользы при этом не несут
                api.middleware = buildMiddlewareForServer({ api: anyApi, apiUid: context.apiUid });
            }

            return {
                injectEndpoint(endpointName, definition) {
                    if (isQueryDefinition(definition)) {
                        if (process.env.NODE_ENV !== 'production') {
                            validateKeepUnusedDataFor(definition.keepUnusedDataFor);
                        }

                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                        const anyEndpoint = anyApi.endpoints[endpointName] as ApiEndpointQuery<any, any> &
                            QueryHooks<any> &
                            EndpointQuery<any> &
                            EndpointQueryFlow<any> &
                            EndpointPrefetch<any> &
                            EndpointSubscribe<any> &
                            EndpointUtils<any> &
                            QueryEndpointCachedValues<any>;

                        const buildPrefetchKey = buildPrefetchKeyBuilder({
                            api: anyApi,
                            endpointName,
                            endpointDefinition: definition,
                            serializeQueryArgs: internalSerializeQueryArgs,
                        });

                        // Добавляем поддержку Suspense в api.useEndpointNameQuery и api.endpoints.endpointName.useQuery
                        const useSsrCompatibleQuery = buildSSRCompatibleQueryHook({
                            api: anyApi,
                            endpointName,
                            getStore,
                            buildPrefetchKey,
                        });
                        const hookName = buildHookName(endpointName);
                        anyApi[hookName] = useSsrCompatibleQuery;
                        anyEndpoint.useQuery = useSsrCompatibleQuery;

                        // Метод для запуска запроса из thunks
                        anyEndpoint.query = buildEndpointQuery({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Метод для создания потока для запроса
                        const query = buildEndpointQueryFlow({
                            api: anyApi,
                            endpointName,
                            getStore,
                            buildPrefetchKey,
                        });
                        const methodName = buildQueryMethodName(endpointName);
                        anyApi[methodName] = query;
                        anyEndpoint.queryFlow = query;

                        // Добавляем метод для предзагрузки данных
                        anyEndpoint.prefetch = buildEndpointPrefetch({
                            api: anyApi,
                            endpointName,
                            getStore,
                            buildPrefetchKey,
                        });

                        // Добавляем метод для подписки на данные
                        anyEndpoint.subscribe = buildEndpointSubscribe({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Добавляем метод для чтения кеша
                        anyEndpoint.getCachedValue = buildEndpointQueryGetCachedValue({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Добавляем метод для подписки на кеш
                        anyEndpoint.useCachedValues = buildEndpointQueryUseCachedValues({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Добавляем методы для работы с кешом
                        const { updateQueryData, upsertQueryData, patchQueryData } = buildEndpointUtils({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        anyEndpoint.updateQueryData = updateQueryData;
                        anyEndpoint.upsertQueryData = upsertQueryData;
                        anyEndpoint.patchQueryData = patchQueryData;
                    } else {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                        const anyEndpoint = anyApi.endpoints[endpointName] as ApiEndpointMutation<any, any> &
                            MutationHooks<any> &
                            EndpointMutate<any> &
                            EndpointMutationState &
                            MutationEndpointCachedValues<any>;

                        // Метод для запуска мутации из thunks
                        const mutate = buildEndpointMutate({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });
                        const methodName = buildMutationMethodName(endpointName);
                        anyApi[methodName] = mutate;
                        anyEndpoint.mutate = mutate;

                        // Метод для отслеживания состояния мутации
                        anyEndpoint.mutationState = buildEndpointMutationState({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Добавляем метод для чтения кеша
                        anyEndpoint.getCachedValue = buildEndpointMutationGetCachedValue({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });

                        // Добавляем метод для подписки на кеш
                        anyEndpoint.useCachedValues = buildEndpointMutationUseCachedValues({
                            api: anyApi,
                            endpointName,
                            getStore,
                        });
                    }
                },
            };
        },
    };
}
